import { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Strategic Stewardship';
const DESCRIPTION =
    'A Christian guide to maximizing your impact: what Scripture teaches about generosity, firstfruits, and cheerful giving of time, talents, and treasure.';

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/2-generosity.jpg',
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: { '@id': 'https://devotionfund.com/resources/strategic-stewardship', '@type': 'WebPage' },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'What does the Bible mean by giving cheerfully?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: '2 Corinthians 9:7 teaches that each person should give what they have decided in their heart, "not reluctantly or under compulsion, for God loves a cheerful giver." Generosity is meant to flow from gratitude and joy, not guilt or pressure.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is the biblical principle of firstfruits?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Firstfruits is the practice of giving the first and best portion of what you receive back to God, referenced throughout the Old Testament (Proverbs 3:9, Leviticus 27:2). It's a way of putting God first in your finances rather than giving only what's left over.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Does Christian stewardship only apply to money?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "No — Scripture calls believers to steward their time, talents, and skills, not just their treasure. The Parable of the Talents (Matthew 25:14-30) is specifically about faithfully using what you've been entrusted with, and volunteering or serving with a professional skill counts as real stewardship alongside financial giving.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What is the Parable of the Talents about?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "In Matthew 25:14-30, a master entrusts his servants with talents (units of currency) and expects them to put those resources to faithful use rather than bury them. It's a teaching about actively multiplying and stewarding what God has given you, whether that's money, time, or ability.",
                    },
                },
            ],
        },
    ],
};

export default function StrategicStewardship() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/strategic-stewardship' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-strategic-stewardship-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/2-generosity.jpg" alt="Strategic Stewardship" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">Strategic Stewardship: A Christian Guide to Maximizing Your Impact</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>The call to generosity is woven throughout the Bible, serving as a cornerstone of Christian life. It's not about pressuring us to empty our wallets, but about cultivating hearts of cheerful stewardship, recognizing that everything we have ultimately belongs to God.</p>
                    <p>Here are some key scriptures that illuminate the importance of giving:</p>
                    <ul>
                        <li><strong>Stewardship:</strong> In 1 Chronicles 29:11-16, King David acknowledges that everything comes from God and we simply return a portion back to Him in worship. We are stewards, entrusted with managing God's blessings.</li>
                        <li><strong>The Principle of Firstfruits:</strong> The concept of giving the first and best portions of our harvest is mentioned throughout the Old Testament (Proverbs 3:9, Leviticus 27:2). This act symbolizes our dependence on God's provision and our desire to honor Him with our resources.</li>
                        <li><strong>Giving Cheerfully:</strong> 2 Corinthians 9:7 beautifully captures the heart of Christian giving: "Each man should give what he has decided in his heart to give, not reluctantly or under compulsion, for God loves a cheerful giver." Our generosity should be a joyful expression of gratitude, not a burden.</li>
                        <li><strong>The Parable of the Talents:</strong> In Matthew 25:14-30, Jesus teaches about the importance of using our God-given gifts and resources faithfully. Like the servants entrusted with talents (a unit of currency), we are called to multiply what we have been given.</li>
                        <li><strong>Giving to the Poor and Vulnerable:</strong> The Bible is filled with verses urging us to care for the less fortunate. Deuteronomy 15:7-8 instructs us to "open your hand to your poor brother" and promises blessings for those who do. James 1:27 emphasizes "looking after orphans and widows in their distress."
                        </li>
                    </ul>
                    <p><strong>Beyond the Offering Plate:</strong></p>
                    <p>The Bible encourages generosity beyond just financial giving. Our time, talents, and skills are also valuable resources we can offer in service to God and others. Consider volunteering at a local soup kitchen, mentoring a young person, or using your professional skills to support a Christian ministry.</p>
                    <p><strong>Remember:</strong> Giving is a spiritual discipline that cultivates gratitude, compassion, and a deeper connection to God. By faithfully practicing stewardship, we reflect God's generosity in the world and experience the joy of giving back.</p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/strategic-stewardship" />
                </div>
            </div>
            <Footer />
        </div>
    );
}


